import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Upload, Search, Send, Check } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import AppStoreBanner from "@/components/AppStoreBanner";

const Step = ({ n, icon: Icon, title, body }) => (
  <div className="relative bg-white border border-[#E2E8E4] rounded-xl p-6 hover:-translate-y-1 transition-transform duration-200 ease-out">
    <div className="overline">Step {n}</div>
    <Icon className="w-6 h-6 mt-3 text-[#2D4A3E]" strokeWidth={1.5} />
    <h3 className="font-display font-semibold text-lg mt-4 tracking-tight">{title}</h3>
    <p className="text-sm text-[#5C6661] mt-2 leading-relaxed">{body}</p>
  </div>
);

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#FDFDFB]">
      <AppStoreBanner />
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-[#E2E8E4]">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center">
            <Logo />
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-[#5C6661]">
            <Link to="/pricing" className="hover:text-[#121413]" data-testid="nav-pricing">Pricing</Link>
            <a href="#how" className="hover:text-[#121413]">How it works</a>
            <Link to="/privacy" className="hover:text-[#121413]" data-testid="nav-privacy">Privacy</Link>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <Button onClick={() => nav("/app")} className="rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]" data-testid="cta-open-app">Open App <ArrowRight className="w-4 h-4 ml-1" /></Button>
            ) : (
              <>
                <Link to="/login" data-testid="header-signin"><Button variant="ghost" className="rounded-full">Sign in</Button></Link>
                <Link to="/register" data-testid="header-getstarted"><Button className="rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]">Get started</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-block overline border border-[#E2E8E4] rounded-full px-3 py-1 bg-white/60 mb-6">
              Speculative applications, done right
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#121413] leading-[1.05]">
              Send your CV to companies that fit —<span className="text-[#2D4A3E]"> whether or not they&apos;re hiring.</span>
            </h1>
            <p className="mt-6 text-lg text-[#5C6661] leading-relaxed max-w-xl">
              Upload your CV once. JobKnocker finds companies that match your profile and emails your application from your own inbox with an optional cover letter.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={user ? "/app" : "/register"} data-testid="hero-cta">
                <Button size="lg" className="rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50] px-8">
                  {user ? "Open dashboard" : "Upload your CV"} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/pricing" data-testid="hero-pricing"><Button variant="outline" size="lg" className="rounded-full border-[#E2E8E4]">See pricing</Button></Link>
            </div>
            <div className="mt-8 text-xs text-[#5C6661]">Works on iPhone, Android and web · sends from Gmail, Outlook, iCloud, Fastmail — any SMTP.</div>
          </div>

          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1455390582262-044cdead277a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200"
              alt="Handwritten letter and pen — speculative applications"
              className="rounded-2xl w-full aspect-[4/5] object-cover border border-[#E2E8E4]"
            />
            <div className="absolute -bottom-6 -left-6 bg-white border border-[#E2E8E4] rounded-xl p-4 shadow-[0_10px_40px_rgba(0,0,0,0.06)] w-64">
              <div className="text-xs text-[#5C6661]">Matched this week</div>
              <div className="font-display text-3xl font-bold text-[#2D4A3E]">14 companies</div>
              <div className="text-xs text-[#5C6661] mt-1">across your industry</div>
            </div>
          </div>
        </div>
      </section>

      <section id="how" className="max-w-7xl mx-auto px-6 py-20 border-t border-[#E2E8E4]">
        <div className="mb-12">
          <div className="overline">How it works</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">Three quick steps.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <Step n="01" icon={Upload} title="Upload your CV" body="Drop your PDF or DOCX. JobKnocker parses role, industry, skills and experience — no forms." />
          <Step n="02" icon={Search} title="See matching companies" body="A curated shortlist with each company's info, careers or recruitment address ready to go." />
          <Step n="03" icon={Send} title="Send from your inbox" body="Tick the ones you like, add a cover letter, and we send from your own email so replies come to you." />
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-20 border-t border-[#E2E8E4] text-center">
        <div className="overline">Pricing</div>
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">One price. One promise.</h2>
        <p className="text-[#5C6661] mt-3 max-w-xl mx-auto">Try free with 5 matched companies. Go Pro for unlimited discovery and applications.</p>
        <div className="grid md:grid-cols-2 gap-6 mt-10 text-left">
          <div className="rounded-xl border border-[#E2E8E4] bg-white p-8">
            <div className="overline">Free</div>
            <div className="font-display text-4xl font-bold mt-2">£0</div>
            <ul className="mt-6 space-y-3 text-sm text-[#121413]">
              {["CV parsing with AI","Up to 5 matched companies per search","AI-generated cover letters","Send from your own inbox","Sent history"].map((f) => (
                <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#2D4A3E] mt-0.5" />{f}</li>
              ))}
            </ul>
            <Link to="/register" data-testid="free-cta"><Button variant="outline" className="mt-8 w-full rounded-full border-[#E2E8E4]">Get started free</Button></Link>
          </div>
          <div className="rounded-xl border border-[#2D4A3E] bg-[#E7F0EB]/30 p-8 relative">
            <div className="absolute -top-3 left-8 bg-[#2D4A3E] text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-full">Recommended</div>
            <div className="overline text-[#2D4A3E]">JobKnocker Pro</div>
            <div className="font-display text-4xl font-bold mt-2">£4.99<span className="text-lg text-[#5C6661] font-normal"> / month</span></div>
            <ul className="mt-6 space-y-3 text-sm text-[#121413]">
              {["Everything in Free","Unlimited matched companies","Unlimited applications sent","Priority AI cover letters","One subscription — works on iPhone, Android and web"].map((f) => (
                <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#2D4A3E] mt-0.5" />{f}</li>
              ))}
            </ul>
            <Link to="/pricing" data-testid="pro-cta"><Button className="mt-8 w-full rounded-full bg-[#2D4A3E] hover:bg-[#3B5F50]">Go Pro</Button></Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E2E8E4] py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[#5C6661]">
          <div>© {new Date().getFullYear()} JobKnocker · Speculative applications, done right.</div>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-[#121413]">Privacy</Link>
            <Link to="/terms" className="hover:text-[#121413]">Terms</Link>
            <Link to="/pricing" className="hover:text-[#121413]">Pricing</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
